import * as React from 'react'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { FormikContextType } from 'formik'
import { FileMutationDto } from '../../../config/fileConfig'
import useUsers from '../../../hooks/useUsers'
import { UserDto } from '../../../config/userConfig'
import { UserTierAction } from './UserTierList'
import { Field } from '@fluentui/react-components'
import MoneyInput from '../../Input/MoneyInput'
import { UserTierFormProps } from './UserTierForm'
import { BaseTierDto, isNormalTier, TierDto } from '../../../config/finance/financeConfig'
import { CommitEventType } from '../../Input/FormattableNumberInput'
import { usePrevious } from '@fluentui/react-hooks'
import { useTranslation } from 'react-i18next'
import UserPicker from '../../TagPicker/UserPicker'

export type UserBaseTierFormContainerProps<T extends BaseTierDto> = UserTierFormProps<T> & {
    fileContext: FormikContextType<FileMutationDto>
    selectableUsers: UserDto[]
    index: number
    action: UserTierAction
}

type UserTierFormContainerProps = UserBaseTierFormContainerProps<TierDto>

export default function UserTierFormContainer(props: UserTierFormContainerProps): ReactElement {
    const { t: translate } = useTranslation()
    const fileContext = props.fileContext
    const tier = useMemo(
        () => (fileContext.values?.tiers[props.index] as TierDto) ?? props.initialValues,
        [fileContext.values.tiers],
    )
    const prevTierValues = usePrevious(tier)
    const { users } = useUsers()
    const [selectedUser, setSelectedUser] = useState<string[]>([tier?.userId])

    useEffect(() => {
        const userTier = users
            .find(user => user.id === tier?.userId)
            ?.tiers?.filter(tier => isNormalTier(tier))
            .map(tier => tier as TierDto)
            .find(tier => tier?.currency === fileContext.values.currency)

        if (
            (userTier && prevTierValues && tier?.userId !== prevTierValues.userId) ||
            (prevTierValues === undefined && props.action === UserTierAction.ADD)
        ) {
            fileContext.setFieldValue(`tiers[${props.index}].salesRate`, userTier?.salesRate || tier?.salesRate)
            fileContext.setFieldValue(
                `tiers[${props.index}].purchaseRate`,
                userTier?.purchaseRate || tier?.purchaseRate,
            )
        }
    }, [tier?.userId])

    return (
        <>
            <Field label={translate('file.edit.form.finance.rates.tier.userId')} required>
                <UserPicker
                    singleSelect
                    selectedOptions={selectedUser}
                    onOptionsSelect={(_, selectedOptions) => {
                        setSelectedUser(selectedOptions)
                        fileContext.setFieldValue(`tiers[${props.index}].userId`, selectedOptions?.[0])
                    }}
                />
            </Field>
            <Field label={translate('file.edit.form.finance.rates.tier.purchaseRate')}>
                <MoneyInput
                    value={tier?.purchaseRate}
                    currency={tier?.currency}
                    onCommit={(eventType: CommitEventType, value: string) => {
                        if (eventType !== CommitEventType.OnLoad) {
                            fileContext.setFieldValue(`tiers[${props.index}].purchaseRate`, value)
                        }
                    }}
                />
            </Field>
            <Field label={translate('file.edit.form.finance.rates.tier.salesRate')}>
                <MoneyInput
                    value={tier?.salesRate}
                    currency={tier?.currency}
                    onCommit={(eventType: CommitEventType, value: string) => {
                        if (eventType !== CommitEventType.OnLoad) {
                            fileContext.setFieldValue(`tiers[${props.index}].salesRate`, value)
                        }
                    }}
                />
            </Field>
        </>
    )
}
